import { useMemo } from 'react';
import * as React from 'react';

import type { QueryResult } from '@apollo/client';
import type { DocumentNode } from 'graphql';
import { noop } from 'lodash';

import { WebSocketConnectionState } from 'common/constants/webSocketConnectionState';

import type { PollingSourceProps } from './PollingSource/PollingSource';
import PollingSource from './PollingSource/PollingSource';
import type { ReactiveSourceProps, SubscriptionDescriptor } from './ReactiveSource/ReactiveSource';
import ReactiveSource from './ReactiveSource/ReactiveSource';
import ReactiveChecker from './utils/ReactiveChecker/ReactiveChecker';
import type { StoreHelpers } from './utils/WithStoreHelpers/WithStoreHelpers';

export type SourceHelpers<EntityResponse> = StoreHelpers<EntityResponse>;

export type SourceProps<EntityResponse, QueryResponse> = {
  name: string;
  children: (
    entities: EntityResponse[],
    helpers: SourceHelpers<EntityResponse>,
    queryResult: QueryResult<QueryResponse>
  ) => React.ReactNode;
  query: DocumentNode;
  queryVariables?: Record<string, unknown>;
  skip?: boolean;
  subscriptions: {
    create?: SubscriptionDescriptor[];
    update?: SubscriptionDescriptor;
    delete?: SubscriptionDescriptor;
  };

  onEntityCreated?: (entity: EntityResponse, helpers: SourceHelpers<EntityResponse>) => void;
  onEntityUpdated?: (
    newEntity: EntityResponse,
    prevEntity: EntityResponse,
    helpers: SourceHelpers<EntityResponse>
  ) => void;
  onEntityDeleted?: (entity: EntityResponse, helpers: SourceHelpers<EntityResponse>) => void;
  onEntityLoaded?: (entity: EntityResponse, helpers: SourceHelpers<EntityResponse>) => void;
  selectEntities: (resp?: Partial<QueryResponse>) => EntityResponse[];
  selectStaleEntities: (resp?: Partial<QueryResponse>) => EntityResponse[];
  selectEntityId: (entity: EntityResponse) => string;
  selectSyncOn: (entity: EntityResponse) => Date;
};

function Source<EntityResponse, QueryResponse>(props: SourceProps<EntityResponse, QueryResponse>) {
  const { children, ...restProps } = props;
  const sourceProps = useMemo(
    () => ({
      ...restProps,
      onEntityCreated: restProps.onEntityCreated ?? noop,
      onEntityUpdated: restProps.onEntityUpdated ?? noop,
      onEntityDeleted: restProps.onEntityDeleted ?? noop,
      onEntityLoaded: restProps.onEntityLoaded ?? noop,
      subscriptions: {
        create: restProps.subscriptions.create?.map(sub => ({ variables: {}, ...sub })),
        update: restProps.subscriptions.update && {
          variables: {},
          ...restProps.subscriptions.update,
        },
        delete: restProps.subscriptions.delete && {
          variables: {},
          ...restProps.subscriptions.delete,
        },
      },
    }),
    [restProps]
  );

  const { subscriptions, ...pollingSourceProps } = sourceProps;

  return (
    <ReactiveChecker>
      {webSocketState =>
        webSocketState !== WebSocketConnectionState.NOT_AVAILABLE ? (
          <ReactiveSource {...(sourceProps as ReactiveSourceProps<EntityResponse, QueryResponse>)}>
            {children}
          </ReactiveSource>
        ) : (
          <PollingSource {...(pollingSourceProps as PollingSourceProps<EntityResponse, QueryResponse>)}>
            {children}
          </PollingSource>
        )
      }
    </ReactiveChecker>
  );
}

export default Source;
